/**
 * BRXSessionAnalytics
 *
 * Ecommerce analytics helper for BraveRX.
 * Reads from window.BRXSession and embeddables localStorage to build normalized GTM payloads.
 *
 * Does not observe the flow. Does not trigger itself.
 * The embeddables wrapper is responsible for deciding when to call these.
 *
 * Public API:
 *   buildEcommerceItemFromSelection() -> item object | null
 *   buildStep1Payload()               -> payload object | null
 *   buildStep2Payload(flowId)         -> payload object | null
 *   pushEvent(payload)                -> boolean
 *   pushStep1()                       -> boolean
 *   pushStep2(options)                -> boolean
 */

var DEBUG = false;

function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ['[BRXSessionAnalytics]'].concat([].slice.call(arguments)));
}

(function () {

    // ==================================================
    // Ecommerce item resolution (BRXSession)
    // ==================================================

    /**
     * Resolve the canonical ecommerce item from the current BRXSession selection.
     * Prefers variant when present and meaningful; falls back to product.
     * Returns null if session data is missing or insufficient to build a valid item.
     *
     * Price source: priceCents (integer, e.g. 17900) divided by 100.
     */
    function buildEcommerceItemFromSelection() {
        log('buildEcommerceItemFromSelection:start');

        if (!window.BRXSession) {
            log('BRXSession missing');
            return null;
        }

        var product = window.BRXSession.getSelectionProduct();
        var variant = window.BRXSession.getSelectionVariant();

        log('product:', product);
        log('variant:', variant);

        if (!product) {
            log('No product found');
            return null;
        }

        var itemId = null;
        var itemName = null;
        var price = null;

        if (variant && variant.medId) {
            itemId = variant.medId;
            itemName = (product.title && variant.title)
                ? product.title + ' (' + variant.title + ')'
                : variant.title || product.title || null;
            price = variant.priceCents != null ? variant.priceCents / 100
                : product.priceCents != null ? product.priceCents / 100
                : null;
            log('Using variant path');
        } else {
            itemId = product.medId || null;
            itemName = product.title || null;
            price = product.priceCents != null ? product.priceCents / 100 : null;
            log('Using product fallback path');
        }

        log('Resolved item:', { itemId, itemName, price });

        if (!itemId || price == null) {
            log('Item invalid → returning null');
            return null;
        }

        return {
            item_id: String(itemId),
            item_name: itemName || '',
            price: Math.round(price * 100) / 100,
            quantity: 1
        };
    }

    // ==================================================
    // localStorage helpers (internal)
    // ==================================================

    function safeParseLocalStorage(key) {
        try {
            var raw = window.localStorage.getItem(key);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch (e) { return null; }
    }

    /**
     * Resolve the embeddables flow data record for the given flowId.
     *
     * Precedence:
     *   1. SavvyFormUserData[flowId]
     *   2. SavvyFormCloudUserData[flowId]
     *   3. first value in SavvyFormUserData  (when flowId absent)
     *   4. first value in SavvyFormCloudUserData  (when flowId absent)
     */
    function getEmbeddablesFlowData(flowId) {
        log('getEmbeddablesFlowData:start', { flowId });

        var sources = ['SavvyFormUserData', 'SavvyFormCloudUserData'];

        for (var s = 0; s < sources.length; s++) {
            var key = sources[s];
            var store = safeParseLocalStorage(key);

            log('checking store:', key, store);

            if (!store || typeof store !== 'object') continue;

            if (flowId && store[flowId]) {
                log('Found flow via flowId:', flowId);
                return store[flowId];
            }

            if (!flowId) {
                var keys = Object.keys(store);
                if (keys.length > 0) {
                    log('Using first flow key:', keys[0]);
                    return store[keys[0]];
                }
            }
        }

        log('No flow data found');
        return null;
    }

    // ==================================================
    // Normalization helpers (internal)
    // ==================================================

    /**
     * "01 / 01 / 1994" → "19940101"
     * Returns null if the value cannot be parsed into MM/DD/YYYY.
     */
    function normalizeBirthday(value) {
        if (!value) return null;
        var parts = String(value).split('/');
        if (parts.length !== 3) return null;
        var mm   = parts[0].trim().replace(/\D/g, '');
        var dd   = parts[1].trim().replace(/\D/g, '');
        var yyyy = parts[2].trim().replace(/\D/g, '');
        if (mm.length !== 2 || dd.length !== 2 || yyyy.length !== 4) return null;
        return yyyy + mm + dd;
    }

    /**
     * "male" → "M", "female" → "F", anything else → null.
     */
    function normalizeSex(value) {
        if (!value) return null;
        var v = String(value).trim().toLowerCase();
        if (v === 'male')   return 'M';
        if (v === 'female') return 'F';
        return null;
    }

    // ==================================================
    // Step 1
    // ==================================================

    /**
     * Build the full step_1 ecommerce payload from the current BRXSession selection.
     * Returns null if item data cannot be resolved — never returns a partial payload.
     *
     * value = item.price * 0.03, rounded to two decimals.
     */
    function buildStep1Payload() {
        var item = buildEcommerceItemFromSelection();
        if (!item) return null;

        var value = Math.round(item.price * 0.03 * 100) / 100;

        return {
            event: 'step_1',
            form: 'embeddables_v1',
            ecommerce: {
                value: value,
                currency: 'USD',
                items: [item]
            }
        };
    }

    /**
     * Build and push the step_1 event.
     * Returns true on success, false if session data was insufficient.
     */
    function pushStep1() {
        return pushEvent(buildStep1Payload());
    }

    // ==================================================
    // Step 2
    // ==================================================

    /**
     * Resolve and normalize user identity fields from embeddables localStorage.
     * Returns null if email is absent — email is the hard gate for step_2.
     *
     * @param {string|null} flowId  The savvy flow ID (data-savvy-id attribute value).
     */
    function buildStep2UserData(flowId) {
        var flow = getEmbeddablesFlowData(flowId || null);
        if (!flow) return null;

        var email = typeof flow.email === 'string' ? flow.email.trim().toLowerCase() : null;
        if (!email) return null;

        var userData = {
            email: email,
            country: 'US'
        };

        if (flow.entryId)             userData.intake_session_id = String(flow.entryId);
        if (flow.dosable_user_id != null) userData.dosable_user_id = flow.dosable_user_id;

        var birthday = normalizeBirthday(flow.birthday);
        if (birthday) userData.birthday = birthday;

        var sex = normalizeSex(flow.sex);
        if (sex) userData.sex = sex;

        if (flow.state) userData.state = String(flow.state).trim().toUpperCase();

        return userData;
    }

    /**
     * Build the full step_2 payload.
     * Returns null unless both ecommerce item and email are resolved.
     *
     * @param {string|null} flowId  The savvy flow ID passed from the wrapper.
     */
    function buildStep2Payload(flowId) {
        var item = buildEcommerceItemFromSelection();
        if (!item) return null;

        var userData = buildStep2UserData(flowId || null);
        if (!userData) return null;

        var value = Math.round(item.price * 0.03 * 100) / 100;

        var payload = {
            event: 'step_2',
            form: 'embeddables_v1',
            user_data: userData,
            ecommerce: {
                value: value,
                currency: 'USD',
                items: [item]
            }
        };

        // intake_session_id belongs at top level, not nested inside user_data
        if (userData.intake_session_id) {
            payload.intake_session_id = userData.intake_session_id;
            delete payload.user_data.intake_session_id;
        }

        return payload;
    }

    /**
     * Build and push the step_2 event.
     * @param {{ flowId?: string }} options
     * Returns true on success, false if data was insufficient.
     */
    function pushStep2(options) {
        var flowId = options && options.flowId ? options.flowId : null;
        return pushEvent(buildStep2Payload(flowId));
    }

    // ==================================================
    // Step 3
    // ==================================================

    /**
     * Build the full step_3 payload.
     * Same shape as step_2 but value = price * 0.1.
     * Returns null unless both ecommerce item and email are resolved.
     *
     * @param {string|null} flowId  The savvy flow ID passed from the wrapper.
     */
    function buildStep3Payload(flowId) {
        var item = buildEcommerceItemFromSelection();
        if (!item) return null;

        var userData = buildStep2UserData(flowId || null);
        if (!userData) return null;

        var value = Math.round(item.price * 0.1 * 100) / 100;

        var payload = {
            event: 'step_3',
            form: 'embeddables_v1',
            user_data: userData,
            ecommerce: {
                value: value,
                currency: 'USD',
                items: [item]
            }
        };

        if (userData.intake_session_id) {
            payload.intake_session_id = userData.intake_session_id;
            delete payload.user_data.intake_session_id;
        }

        return payload;
    }

    /**
     * Build and push the step_3 event.
     * @param {{ flowId?: string }} options
     * Returns true on success, false if data was insufficient.
     */
    function pushStep3(options) {
        var flowId = options && options.flowId ? options.flowId : null;
        return pushEvent(buildStep3Payload(flowId));
    }

    // ==================================================
    // dataLayer push
    // ==================================================

    /**
     * Push a pre-built payload to window.dataLayer.
     * Creates dataLayer if it does not already exist.
     * Returns false if payload is falsy.
     */
    function pushEvent(payload) {
        if (!payload) return false;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        return true;
    }

    window.BRXSessionAnalytics = {
        buildEcommerceItemFromSelection: buildEcommerceItemFromSelection,
        buildStep1Payload: buildStep1Payload,
        buildStep2Payload: buildStep2Payload,
        buildStep3Payload: buildStep3Payload,
        pushEvent: pushEvent,
        pushStep1: pushStep1,
        pushStep2: pushStep2,
        pushStep3: pushStep3
    };
})();
